import { useEffect, useState, useImperativeHandle } from "react";
import { Flex, Select, Checkbox } from "@theorgchartdev/storybook";
import { buildTimeOptions } from "@/Utils/Misc";
import SectionCard, {
  SectionCardGroup
} from "@/appComponents/Blocks/Composites/SectionCard/SectionCard";
import { GetWSMgr } from "@/Utils/StateLib";
import {
  formatOptions,
  monthlyOptions,
  getExportOptions,
  buildRefreshOptions,
  getRefreshOptions,
  validateDayValue,
  convertEmailList,
  validateDayOfMonthValue,
  updateFrequency,
  REFRESH_TEST_PREFIX
} from "./utils";
import { DynamicInputComposite } from "@/appComponents/Blocks/Composites/DynamicInputComposite/DynamicInputComposite";
import SectionPrompt from "@/appComponents/Blocks/Composites/SectionPrompt/SectionPrompt";
import { appstr } from "@coreapp/utils/appstr";
import { ValidateEmailAddrList } from "@coreapp/utils/ValidateEmailAddrList";
import { ValidateEmailAddr } from "@coreapp/utils/ValidateEmailAddr";
import { kDayList, kFreqOpts, enumFeaturesList } from "@coreapp/constants";
import { usePermissionCheck } from "@/hooks/usePermissionCheck";
import UpgradeCard from "@/appComponents/Blocks/Composites/UpgradePopover/UpgradeCard";
import { UpgradeInfoCard } from "@/appComponents/Blocks/Composites/UpgradePopover/UpgradeInfoCard";
import { upgradeCardStyle } from "@/Utils/Constants";

export const RefreshConfiguration = ({ chartConfig, handleUpdate, validationRef }) => {
  const [emailsObj, setEmailsObj] = useState({});
  const { showPricingAndPackaging, canAutoExport } = usePermissionCheck();

  useImperativeHandle(validationRef, () => ({
    DoValidate
  }));

  const DoValidate = () => {
    let proceed = true;
    let alertMsg = "";
    let headerMsg = "";
    let alertaccept = appstr("OK");
    if (emailsObj["notifications"]) {
      if (!ValidateEmailAddrList(emailsObj["notifications"])) {
        proceed = false;
        alertMsg = appstr("invalidemailmessage");
        headerMsg = appstr("Invalid email");
        return {
          proceed: proceed,
          alertmsg: alertMsg,
          headermsg: headerMsg,
          alertAccept: alertaccept
        };
      }
    }
    if (emailsObj["exports"]) {
      if (!ValidateEmailAddrList(emailsObj["exports"])) {
        proceed = false;
        alertMsg = appstr("autoemailmessage");
        headerMsg = appstr("Invalid email");
        return {
          proceed: proceed,
          alertmsg: alertMsg,
          headermsg: headerMsg,
          alertAccept: alertaccept
        };
      }
    }

    return { proceed: proceed, alertmsg: alertMsg, headermsg: headerMsg, alertAccept: alertaccept };
  };

  useEffect(() => {
    let wsMgr = GetWSMgr();
    const eventTracker = wsMgr.GetUsageTracker();
    eventTracker.ReportEvent("chart_settings.refresh.opened");
    setEmailsObj(
      chartConfig?.Emails
        ? convertEmailList(chartConfig.Emails)
        : { notifications: [], exports: [] }
    );
  }, [chartConfig?.ID]);

  if (!chartConfig) return;

  const addNewEmail = (email, value, listKey) => {
    const newEmails = emailsObj;
    if (value !== null) email = `${email},${value}`;
    newEmails[listKey].push(email);
    setEmailsObj({ ...newEmails });
  };

  const handleEmailChange = (index, input, listKey) => {
    const value = emailsObj[listKey][index]?.split(",")[1];
    const newEmail = value ? `${input},${value}` : input;
    let newEmails = emailsObj;
    newEmails[listKey][index] = newEmail;
    if (input.length !== 0 && input.length < 4) {
      setEmailsObj({ ...newEmails });
    } else if (!ValidateEmailAddr(input)) {
      setEmailsObj({ ...newEmails });
    }
    if (ValidateEmailAddr(input)) {
      handleUpdate({ ...chartConfig, Emails: newEmails }, "Emails");
    }
  };

  const handleDeleteEmail = (index, listKey) => {
    let newEmails = emailsObj;
    newEmails[listKey].splice(index, 1);
    handleUpdate({ ...chartConfig, Emails: newEmails }, "Emails");
  };

  const changeFrequency = (target, key) => {
    handleUpdate(
      { ...chartConfig, sync: { ...chartConfig.sync, ...updateFrequency(target.value, key) } },
      "sync"
    );
  };

  const changeDayOfMonth = (target, key) => {
    handleUpdate(
      {
        ...chartConfig,
        sync: { ...chartConfig.sync, [key]: validateDayOfMonthValue(target.value) }
      },
      "sync"
    );
  };
  const changeDay = (target, key) => {
    handleUpdate(
      { ...chartConfig, sync: { ...chartConfig.sync, [key]: validateDayValue(target.value) } },
      "sync"
    );
  };
  const changeTime = target => {
    handleUpdate({ ...chartConfig, sync: { ...chartConfig.sync, syncHour: target.value } }, "sync");
  };

  const toggleExport = event => {
    if (event.target.checked) {
      handleUpdate(
        {
          ...chartConfig,
          sync: {
            ...chartConfig.sync,
            exportFreq: "Weekly",
            exportDay: chartConfig.sync?.exportDay || "Wednesday",
            exportType: chartConfig.sync?.exportType || "XLS",
            exportDest: chartConfig.sync?.exportDest || "Email"
          }
        },
        "sync"
      );
    } else {
      handleUpdate({ ...chartConfig, sync: { ...chartConfig.sync, exportFreq: "Off" } }, "sync");
    }
  };

  const toggleNotify = index => {
    const newEmails = { ...emailsObj };
    const [email, notifyStatus] = newEmails.notifications[index]?.split(",");
    newEmails.notifications[index] = `${email},${notifyStatus === "1" ? "0" : "1"}`;
    if (ValidateEmailAddr(email)) handleUpdate({ ...chartConfig, Emails: newEmails }, "Emails");
  };

  const NotificationToggleButtons = index => {
    const value = emailsObj.notifications[index]?.split(",")[1];
    return (
      <Flex direction={"row"} gap={"16px"}>
        <div style={{ width: "200px" }}>
          <Select
            inputOnly={true}
            options={notificationOptions(index)}
            placeholder={appstr("Notify on")}
            value={value}
            selectSize={"medium"}
            id={"cs-ref-time-select"}
            dataTestId={`${REFRESH_TEST_PREFIX}-notify-on-select-${index}`}
          />
        </div>
      </Flex>
    );
  };

  const notificationOptions = index => {
    let options = [
      {
        label: appstr("Success and failure"),
        value: "1",
        clickHandler: () => toggleNotify(index)
      },
      {
        label: appstr("Failure only"),
        value: "0",
        clickHandler: () => toggleNotify(index)
      }
    ];

    return options;
  };

  const changeFormat = target => {
    handleUpdate(
      {
        ...chartConfig,
        sync: { ...chartConfig.sync, exportType: target.value }
      },
      "sync"
    );
  };
  const changeExportDest = target => {
    handleUpdate(
      {
        ...chartConfig,
        sync: { ...chartConfig.sync, exportDest: target.value }
      },
      "sync"
    );
  };

  const RefreshFrequencySelect = (key, value) => {
    let options = kFreqOpts;

    if (key === "exportFreq") {
      options = options.filter(item => item !== "Daily" && item !== "Off"); //off managed by checkbox
    }

    return (
      <Flex direction={"column"} width={"400px"}>
        <Select
          inputOnly={false}
          label={appstr("frequency")}
          options={buildRefreshOptions(changeFrequency, options, key)}
          placeholder={appstr("Select frequency")}
          value={value}
          selectSize={"medium"}
          id={`cs-${key}-frequency`}
          dataTestId={`${REFRESH_TEST_PREFIX}-${key}-frequency-select`}
        />
      </Flex>
    );
  };

  const RefreshTimeSelect = value => {
    return (
      <Flex direction={"column"} width={"400px"}>
        <Select
          inputOnly={false}
          label={appstr("gmttime")}
          options={buildTimeOptions(changeTime, true, true)}
          placeholder={`${appstr("Select")} ${appstr("Time")}`}
          value={value}
          selectSize={"medium"}
          id={"cs-ref-time-select"}
          dataTestId={`${REFRESH_TEST_PREFIX}-schedule-time-select`}
          listProps={{
            inputProps: {
              placeholder: appstr("Search")
            }
          }}
        />
      </Flex>
    );
  };

  const RefreshDaySelect = (key, value) => {
    return (
      <Flex direction={"column"} width={"400px"}>
        <Select
          inputOnly={false}
          label={appstr("Day")}
          options={buildRefreshOptions(changeDay, kDayList, key)}
          placeholder={`${appstr("Select")} ${appstr("Day")}`}
          value={validateDayValue(value)}
          selectSize={"medium"}
          id={`cs-day-select-${key}`}
          dataTestId={`${REFRESH_TEST_PREFIX}-${key}-select`}
        />
      </Flex>
    );
  };

  const RefreshDayOfMonthSelect = (key, value) => {
    return (
      <Flex direction={"column"} width={"400px"}>
        <Select
          inputOnly={false}
          label={appstr("Day")}
          options={buildRefreshOptions(changeDayOfMonth, monthlyOptions, key)}
          placeholder={`${appstr("Select")} ${appstr("Day")}`}
          value={value}
          selectSize={"medium"}
          id={`cs-day-of-month-select-${key}`}
          dataTestId={`${REFRESH_TEST_PREFIX}-${key}-of-month-select`}
        />
      </Flex>
    );
  };

  const FrequencySectionContent = (frequency, key, value) => {
    let content;
    const dayKey = key === "schedule" ? "day" : "exportDay";

    switch (frequency) {
      case "Daily":
        content = RefreshTimeSelect(chartConfig.sync?.syncHour);
        break;
      case "Weekly": {
        content = (
          <>
            {RefreshDaySelect(dayKey, value)}
            {key === "schedule" && RefreshTimeSelect(chartConfig.sync?.syncHour)}
          </>
        );
        break;
      }
      case "Monthly":
        content = <>{RefreshDayOfMonthSelect(dayKey, value)}</>;
        break;
      default:
        content = null;
        break;
    }

    return (
      <>
        {RefreshFrequencySelect(key, frequency)}
        {content}
      </>
    );
  };

  const notificationEmails = emailsObj?.notifications?.map(item => item.split(",")[0]) || [];
  const exportEmails = emailsObj?.exports || [];
  let customCardStyle = {};
  if (!canAutoExport) {
    customCardStyle = { ...upgradeCardStyle };
  }
  let autoExportMessageDynamicText = {
    template: appstr("autoExportUpgradeTemplate"),
    formatConfig: {
      bold1: { type: "bold" }
    },
    replaceDict: {
      bold1: appstr("automatically export")
    }
  };

  return (
    <SectionCardGroup>
      <SectionCard title={appstr("Schedule Chart Refresh")}>
        <Flex direction={"column"} gap={"16px"}>
          <Flex direction={"row"} gap={"8px"}>
            {FrequencySectionContent(chartConfig.sync?.schedule, "schedule", chartConfig.sync?.day)}
          </Flex>
          {chartConfig.sync?.schedule !== "Off" && (
            <SectionPrompt
              title={appstr("Automatic refresh notifications")}
              description={appstr("refreshemail")}
            >
              <DynamicInputComposite
                id={"cs-notification-emails-"}
                values={notificationEmails}
                actionContent={NotificationToggleButtons}
                onInput={(index, input) => handleEmailChange(index, input, "notifications")}
                onDelete={index => handleDeleteEmail(index, "notifications")}
                onAdd={() => addNewEmail("", 0, "notifications")}
                inputProps={{
                  size: "medium",
                  placeholder: appstr("pwemail"),
                  dataTestId: `${REFRESH_TEST_PREFIX}-notification-email-input`
                }}
              />
            </SectionPrompt>
          )}
        </Flex>
      </SectionCard>
      {(canAutoExport || showPricingAndPackaging) && (
        <SectionCard title={appstr("Automated Chart Export")} customCardStyle={customCardStyle}>
          {!canAutoExport && (
            <UpgradeCard iconName={"file-export"} featureKey={enumFeaturesList.autoExport}>
              <UpgradeInfoCard.Body dynamic={autoExportMessageDynamicText}>
                {appstr("autoExportUpgradeMessage")}
              </UpgradeInfoCard.Body>
            </UpgradeCard>
          )}
          {canAutoExport && (
            <Flex direction={"column"} gap={"16px"}>
              <Checkbox
                id={"cs-toggle-export"}
                data-testid={`${REFRESH_TEST_PREFIX}-auto-export-toggle`}
                label={appstr("Enable auto-export")}
                size={"medium"}
                checked={!!chartConfig.sync?.exportFreq && chartConfig.sync.exportFreq !== "Off"}
                onToggle={toggleExport}
                disabled={chartConfig.sync?.schedule === "Off"}
              />
              {!!chartConfig.sync?.exportFreq && chartConfig.sync.exportFreq !== "Off" && (
                <>
                  <Flex direction={"row"} gap={"8px"}>
                    {FrequencySectionContent(
                      chartConfig.sync?.exportFreq,
                      "exportFreq",
                      chartConfig.sync?.exportDay
                    )}
                  </Flex>
                  <Flex direction={"row"} gap={"8px"}>
                    <Flex direction={"column"} width={"400px"}>
                      <Select
                        inputOnly={false}
                        label={appstr("Export format")}
                        options={getRefreshOptions(changeFormat, formatOptions)}
                        placeholder={appstr("Select format")}
                        value={chartConfig.sync?.exportType}
                        selectSize={"medium"}
                        id={"cs-export-format-select"}
                        dataTestId={`${REFRESH_TEST_PREFIX}-export-format-select`}
                      />
                    </Flex>
                    <Flex direction={"column"} width={"400px"}>
                      <Select
                        inputOnly={false}
                        label={appstr("Destination")}
                        options={getRefreshOptions(changeExportDest, getExportOptions())}
                        placeholder={appstr("pubdest")}
                        value={chartConfig.sync?.exportDest}
                        selectSize={"medium"}
                        id={"cs-export-dest-select"}
                        dataTestId={`${REFRESH_TEST_PREFIX}-export-destination-select`}
                      />
                    </Flex>
                  </Flex>
                  <SectionPrompt description={appstr("autoexportemail")}>
                    <DynamicInputComposite
                      id={"cs-export-emails-"}
                      values={exportEmails}
                      onInput={(index, input) => handleEmailChange(index, input, "exports")}
                      onDelete={index => handleDeleteEmail(index, "exports")}
                      onAdd={() => addNewEmail("", null, "exports")}
                      inputProps={{
                        size: "medium",
                        placeholder: appstr("pwemail"),
                        dataTestId: `${REFRESH_TEST_PREFIX}-export-email-input`
                      }}
                    />
                  </SectionPrompt>
                </>
              )}
            </Flex>
          )}
        </SectionCard>
      )}
    </SectionCardGroup>
  );
};
